import {
  flat,
  isStampName,
  isTombstone,
  isUntitled,
  parseEntity,
  picksIn,
  sectionNamed,
  sections,
  transcriptTopics,
} from '../entity/entity';
import { durationInNote, rowsPathFor } from './duration';

/**
 * A capture, reduced to what a row shows.
 *
 * The face is the recognition device — the few picked memory triggers, which
 * is what brings a recording back at a glance. A third of the corpus has
 * none, so the first line of the body stands in; it is the raw words rather
 * than the point, and the row says so rather than passing it off as a face.
 */

export interface Thought {
  path: string;
  /** A name someone gave it, or null when the filename is only a timestamp. */
  title: string | null;
  face: string[];
  standIn: string;
  topics: number;
  /** A deletion marker: a real file under the original capture's name, with
   *  an empty body. Only the file says so — the manifest cannot. */
  tombstone: boolean;
  /** How long its audio runs, where that is known. */
  durationMs: number | null;
  /** The rows sidecar that says how long it runs, where the note itself does
   *  not. */
  rowsAt: string | null;
}

/** How many words a recording's opening sentence runs to before it is cut, as the app cuts it. */
const OPENING_WORDS = 25;

/** [text]'s first sentence, cut at [OPENING_WORDS] words. */
export function openingSentence(text: string): string {
  const sentence = text.trim().split(/(?<=[.!?…])\s+/)[0] ?? '';
  const words = sentence.split(/\s+/).filter(Boolean);
  return words.length > OPENING_WORDS ? `${words.slice(0, OPENING_WORDS).join(' ')}…` : sentence;
}

/**
 * The one line a recording goes by among other things, the app's `sourceWords`: a name someone
 * gave it ([name], the catalog's, before the file's own), else its first pick, else its first
 * suggestion, else its opening sentence. Never the time it was made.
 */
export function recordingLine(path: string, raw: string, name?: string | null): string {
  const given = [name, readThought(path, raw).title].find((n): n is string => !!n && n.trim() !== '' && !isStampName(n));
  if (given) return given.trim();
  const thought = readThought(path, raw);
  if (thought.face[0]) return thought.face[0];
  const suggested = picksIn(sectionNamed(parseEntity(path, raw), 'suggestions')?.body ?? '')
    .map((p) => p.text)
    .find(isSpoken);
  if (suggested) return suggested;
  return openingSentence(thought.standIn);
}

/** A path that turned out to be a deletion marker, or to resolve to nothing. */
export function tombstoneAt(path: string): Thought {
  return { path, title: null, face: [], standIn: '', topics: 0, tombstone: true, durationMs: null, rowsAt: null };
}

export function readThought(path: string, raw: string): Thought {
  const entity = parseEntity(path, raw);
  if (isTombstone(entity)) return tombstoneAt(path);
  const durationMs = durationInNote(raw);
  const id = flat(entity.frontMatter['id']).trim();

  return {
    path,
    title: isUntitled(entity) ? null : entity.title,
    face: picksIn(sectionNamed(entity, 'face')?.body ?? '')
      .map((p) => p.text)
      .filter(isSpoken),
    standIn: standInFor(entity),
    topics: sections(entity).length,
    tombstone: false,
    durationMs,
    rowsAt: durationMs === null && id && raw.includes('<!--pondering:unit ') ? rowsPathFor(id) : null,
  };
}

/**
 * Exports up to 2026-05 listed the recording's AUDIO block among the picks,
 * and its text is the message payload — `{"uri":…,"durationMs":…}`. 1,402
 * files in the vault carry one, so an unfiltered face opens a third of the
 * corpus with a JSON blob where the first spoken line should be.
 */
function isSpoken(text: string): boolean {
  return !text.trimStart().startsWith('{"');
}

function standInFor(entity: ReturnType<typeof parseEntity>): string {
  const spoken = transcriptTopics(entity)[0]?.paragraphs[0];
  if (spoken) return spoken;

  // A typed note has no transcript, so the body itself is the words. Headings
  // are the export's own structure and never stand in for what was said.
  return (
    entity.body
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line !== '' && !line.startsWith('#') && !line.startsWith('<!--')) ?? ''
  );
}
